import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * SPEC 4.0 / 5.10 — three top-level mode toggles.
 * Persisted to localStorage so the inline bootstrap script in index.html
 * can apply them before first paint (FOUC).
 *
 * Storage key: "haus.prefs" (must match index.html bootstrap).
 */

export type Theme = "dark" | "light";
export type Density = "comfortable" | "compact";
export type UnitsPreference =
  | "metric"
  | "imperial"
  | "metric_with_imperial_secondary";
/** 0 = Sunday, 1 = Monday, 6 = Saturday — matches date-fns convention. */
export type WeekStart = 0 | 1 | 6;

type PrefsState = {
  theme: Theme;
  density: Density;
  units: UnitsPreference;
  weekStart: WeekStart;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
  setDensity: (d: Density) => void;
  toggleDensity: () => void;
  setUnits: (u: UnitsPreference) => void;
  setWeekStart: (w: WeekStart) => void;
};

export const usePrefs = create<PrefsState>()(
  persist(
    (set, get) => ({
      theme: "dark",
      density: "comfortable",
      units: "metric_with_imperial_secondary",
      weekStart: detectWeekStart(),
      setWeekStart: (weekStart) => set({ weekStart }),
      setTheme: (theme) => {
        set({ theme });
        applyThemeAttr(theme);
      },
      toggleTheme: () => {
        const next: Theme = get().theme === "dark" ? "light" : "dark";
        set({ theme: next });
        applyThemeAttr(next);
      },
      setDensity: (density) => {
        set({ density });
        applyDensityAttr(density);
      },
      toggleDensity: () => {
        const next: Density =
          get().density === "comfortable" ? "compact" : "comfortable";
        set({ density: next });
        applyDensityAttr(next);
      },
      setUnits: (units) => set({ units }),
    }),
    {
      name: "haus.prefs",
      storage: createJSONStorage(() => localStorage),
      version: 3,
      // HOTFIX-BW19.1 Bug 5: Zustand logs "State loaded from storage couldn't
      // be migrated since no migrate function was provided" when version is
      // bumped without one. The shape has not actually changed across
      // versions; persistedState is already a valid PrefsState. No-op pass.
      migrate: (persistedState) => persistedState as PrefsState,
      partialize: (state) => ({
        theme: state.theme,
        density: state.density,
        units: state.units,
        weekStart: state.weekStart,
      }),
    },
  ),
);

/**
 * BW12.5 Issue 6 — locale-aware week-start default.
 *
 * Detection priority:
 *   1. `Intl.Locale#weekInfo.firstDay` (Chrome/Safari 17+, Firefox 119+).
 *   2. Country code heuristic for the dominant locale.
 *   3. Hard fallback to Monday (matches ISO 8601 + the legacy startOfWeek default).
 *
 * Saturday-first locales (most of the Middle East) get 6, Sunday-first locales
 * (US, CA, JP, BR, IL exception aside) get 0, everywhere else 1.
 */
function detectWeekStart(): WeekStart {
  if (typeof navigator === "undefined") return 1;
  try {
    const locale = navigator.language || "en-US";
    type WithWeekInfo = { weekInfo?: { firstDay?: number } };
    const intlLocale = new Intl.Locale(locale) as Intl.Locale & WithWeekInfo;
    const fd = intlLocale.weekInfo?.firstDay;
    if (fd === 7) return 0;
    if (fd === 1) return 1;
    if (fd === 6) return 6;
  } catch {
    // Fall through to heuristic below.
  }
  // Heuristic on country code.
  const lang = (typeof navigator !== "undefined" && navigator.language) || "";
  const country = lang.split("-")[1]?.toUpperCase();
  const SUNDAY = new Set([
    "US", "CA", "MX", "BR", "AR", "CL", "CO", "JP", "PH", "IL", "IN", "PK", "TH",
  ]);
  const SATURDAY = new Set(["AE", "SA", "EG", "QA", "BH", "OM", "JO", "KW"]);
  if (country && SATURDAY.has(country)) return 6;
  if (country && SUNDAY.has(country)) return 0;
  return 1;
}

function applyThemeAttr(theme: Theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

function applyDensityAttr(density: Density) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-density", density);
}
